import { useEffect, useMemo } from 'react';
import { RfUiInput, RfUiSelectSearch } from 'renfe-components';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedInstructors } from 'store/app/actions';
import {
  makeSelectIsStudentProfile,
  makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected,
} from 'store/usuarios/selectors';
import { language } from 'utils/literals';
import { userProfile } from 'utils/profilesIndex';
import { stateLiteral } from 'utils/statesLiterals';
import {
  makeSelectInstructoresPracticas,
  makeSelectPracticaSeleccionada,
} from 'store/practicas/selectors';
import { createStructuredSelector } from 'reselect';
import { parseInstructor } from 'store/practicas/sagas';
import { makeSelectInstructorsSelected } from 'store/app/selectors';
import {
  InstructorPractica,
  PracticaSeleccionada,
} from 'store/practicas/types';

const selectors = createStructuredSelector({
  profileActive: makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
  practicaSeleccionada: makeSelectPracticaSeleccionada(),
  instructoresPracticas: makeSelectInstructoresPracticas(),
  selectedInstructors: makeSelectInstructorsSelected(),
  isStudentProfile: makeSelectIsStudentProfile(),
} as any);

const InstructorsContainer = (props: any) => {
  const dispatch = useDispatch();
  const {
    profileActive,
    practicaSeleccionada,
    instructoresPracticas,
    selectedInstructors,
    isStudentProfile,
  } = useSelector<any, any>(selectors);

  const editMode: boolean = useMemo(
    () => !!practicaSeleccionada.CUERPO_FICHA,
    [practicaSeleccionada]
  );

  const calculatedSelectedInstructorsLabel: string = useMemo(() => {
    const instructorCod = selectedInstructors.length
      ? selectedInstructors[0].value?.ID_USUARIO ?? ''
      : '';
    if (instructorCod && instructoresPracticas) {
      const selectedInstructor: InstructorPractica = instructoresPracticas.find(
        (instructor: InstructorPractica) =>
          instructor.ID_USUARIO === instructorCod
      );
      if (selectedInstructor) {
        return (
          selectedInstructor.ID_USUARIO +
          ' - ' +
          selectedInstructor.NOMBRE_USUARIO
        );
      }
    }
    return '';
  }, [instructoresPracticas, selectedInstructors]);

  const calculatedSelectedInstructorsOptions = useMemo(() => {
    const selectedInstructor = selectedInstructors.length
      ? selectedInstructors[0]
      : null;
    const instructoresActivos = instructoresPracticas.filter(
      (inst: InstructorPractica) => {
        return inst.ACTIVO === 'S';
      }
    );
    const options = instructoresActivos.map(
      (instructor: InstructorPractica) => ({
        label: instructor.ID_USUARIO + ' - ' + instructor.NOMBRE_USUARIO,
        value: instructor,
        active:
          !!selectedInstructor?.value?.ID_USUARIO &&
          selectedInstructor.value.ID_USUARIO === instructor.ID_USUARIO,
      })
    );
    return JSON.stringify(options);
  }, [instructoresPracticas, selectedInstructors]);

  const isManagerProfile: boolean = useMemo(() => {
    return (
      profileActive?.ID_PERFIL === userProfile.formador ||
      profileActive?.ID_PERFIL === userProfile.gestor ||
      profileActive?.ID_PERFIL === userProfile.administrador
    );
  }, [profileActive]);

  const calculatedSocietyName: string = useMemo(() => {
    return practicaSeleccionada.CABECERA_FICHA?.SOCIEDAD_INSTRUCTOR &&
      practicaSeleccionada.CABECERA_FICHA?.NOMBRE_SOCIEDAD_INSTRUCTOR
      ? practicaSeleccionada.CABECERA_FICHA.SOCIEDAD_INSTRUCTOR +
          ' - ' +
          practicaSeleccionada.CABECERA_FICHA.NOMBRE_SOCIEDAD_INSTRUCTOR
      : '';
  }, [practicaSeleccionada]);

  // Condiciones de edición

  const canEditSelectedInstructors: boolean = useMemo(() => {
    const estadoPractica =
      (practicaSeleccionada as PracticaSeleccionada)?.CUERPO_FICHA
        ?.ID_ESTADO_PRACTICA ?? null;
    return (
      isStudentProfile &&
      !!estadoPractica &&
      (estadoPractica === stateLiteral.borrador ||
        estadoPractica === stateLiteral.pendCorrecion)
    );
  }, [isStudentProfile, practicaSeleccionada]);

  // Eventos

  const handleChangeSelectedInstructors = (e: CustomEvent) => {
    // Sólo escuchamos eventos con detail === '' y con detail.value
    if (e.detail === '') {
      dispatch(setSelectedInstructors(parseInstructor('')));
    } else if (e.detail?.value) {
      const instructorCod = e.detail.value.ID_USUARIO;
      dispatch(setSelectedInstructors(parseInstructor(instructorCod)));
    }
  };

  return (
    <>
      {editMode ? (
        <div>
          <h2 className="rf-title rf-title--secundary">
            {language.informacionPractica.instructorLabel}
          </h2>
          <div className="rf-practice-detail__input-container">
            <RfUiSelectSearch
              disabled={!canEditSelectedInstructors}
              options={calculatedSelectedInstructorsOptions}
              textError={language.validations.multiselectEmpty}
              onOptionSelected={handleChangeSelectedInstructors}
              text-label={language.informacionPractica.instructorName}
              placeholder={language.informacionPractica.instructorPlaceholder}
              defaultValue={calculatedSelectedInstructorsLabel}
            />
          </div>

          {isManagerProfile && (
            <div className="rf-practice-detail__input-container">
              <RfUiInput
                label={language.informacionPractica.sociedadInstructor}
                error=""
                className="rf-input"
                disabled
                value={calculatedSocietyName}
              />
            </div>
          )}
        </div>
      ) : (
        <div>
          <h2 className="rf-title rf-title--secundary">
            {language.informacionPractica.instructorLabel}
          </h2>
          <div className="rf-practice-create__input-container">
            <RfUiSelectSearch
              options={calculatedSelectedInstructorsOptions}
              onOptionSelected={handleChangeSelectedInstructors}
              textError={language.validations.multiselectEmpty}
              text-label={language.informacionPractica.instructorName}
              placeholder={
                language.informacionPractica.instructorPlaceholderName
              }
              defaultValue={calculatedSelectedInstructorsLabel}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default InstructorsContainer;
